// `text-angular-toolbar` — the button strip under the message body
// (vendor/textAngular/src/main.js:860-980, plus careero's own registered tools).
//
// ⚠️ THE TOOL LIST IS AN ARRAY OF ARRAYS AND EACH INNER ARRAY IS A `btn-group`. That is not
// decoration: `.btn-group` is what removes the gap and rounds only the outer corners, so
// flattening the four groups into one row gives twelve separate rounded buttons.
import type { ReactElement } from 'react'

/** One entry of textAngular's `taTools` table, narrowed to what rendering needs. */
export interface RichTextTool {
  /** `iconclass` — rendered as `<i class="…">` inside the button. */
  iconClass?: string
  /** `buttontext` — the button's text when it has no icon. */
  buttonText?: string
  /** `tooltiptext` — the `title` attribute. */
  title?: string
}

/**
 * The tools this app registers, by the names its toolbars list.
 *
 * ⚠️ SIX OF THESE ARE CAREERO'S, NOT textAngular'S, and they are registered by DECORATORS in
 * `common/directives/*` rather than in the vendored setup — `insertTokenFirstName`,
 * `insertTokenHunterName`, `directionLTR`, `directionRTL`, `insertImageUpload`,
 * `insertAttachment`. Reading only `textAngularSetup.js` finds the other five and leaves six
 * holes in the strip.
 */
export const RICH_TEXT_TOOLS: Readonly<Record<string, RichTextTool>> = {
  // `buttontext` with no icon — these two render their own literal braces.
  insertTokenFirstName: { buttonText: '{First name}', title: 'Insert candidate/contact first name' },
  insertTokenHunterName: { buttonText: '{Hunter}', title: 'Insert hunter name' },
  // ⚠️ THE LTR ICON IS THE RTL ONE, FLIPPED. `fa-paragraph-rtl` with `fa-flip-horizontal` in
  // front of it — there is no `fa-paragraph-ltr`, and dropping the flip gives two identical
  // buttons whose meanings are opposite. Note the trailing semicolon in the LTR tooltip: it is
  // upstream's, typo and all, and it is on screen.
  directionLTR: { iconClass: 'fa fa-flip-horizontal fa-paragraph-rtl', title: 'Left to right;' },
  directionRTL: { iconClass: 'fa fa-paragraph-rtl', title: 'Right to left' },
  italics: { iconClass: 'fa fa-italic', title: 'Italic' },
  bold: { iconClass: 'fa fa-bold', title: 'Bold' },
  underline: { iconClass: 'fa fa-underline', title: 'Underline' },
  ul: { iconClass: 'fa fa-list-ul', title: 'Unordered List' },
  ol: { iconClass: 'fa fa-list-ol', title: 'Ordered List' },
  insertLink: { iconClass: 'fa fa-link', title: 'Insert / edit link' },
  insertImageUpload: { iconClass: 'fas fa-image', title: 'Insert image' },
  insertAttachment: { iconClass: 'fas fa-paperclip', title: 'Attach files' },
  // `redo` is `fa-repeat`, not `fa-redo` — this is Font Awesome 4's name, and the vendored
  // sheet is the one that has it.
  redo: { iconClass: 'fa fa-repeat', title: 'Redo' },
  undo: { iconClass: 'fa fa-undo', title: 'Undo' },
}

/**
 * `bodyTextAngularToolbar` — the strip beside a message sent FROM AN ALERT.
 *
 * ⚠️ THE ALERTLESS STRIP IS NOT A SUBSET BY ACCIDENT: it drops `insertTokenHunterName`, because
 * there is no hunter to name when the message is composed from a candidate's own profile. Every
 * capture of the profile's composer is the alertless one.
 */
export const BODY_TOOLBAR: ReadonlyArray<readonly string[]> = [
  ['insertTokenFirstName', 'insertTokenHunterName'],
  ['directionLTR', 'directionRTL'],
  ['italics', 'bold', 'underline', 'ul', 'ol', 'insertLink', 'insertImageUpload', 'insertAttachment'],
  ['redo', 'undo'],
]

export const BODY_ALERTLESS_TOOLBAR: ReadonlyArray<readonly string[]> = [
  ['insertTokenFirstName'],
  ['directionLTR', 'directionRTL'],
  ['italics', 'bold', 'underline', 'ul', 'ol', 'insertLink', 'insertImageUpload', 'insertAttachment'],
  ['redo', 'undo'],
]

/** The signature strip: no tokens at all, and no attachment button. */
export const SIGNATURE_TOOLBAR: ReadonlyArray<readonly string[]> = [
  ['directionLTR', 'directionRTL'],
  ['italics', 'bold', 'underline', 'ul', 'ol', 'insertLink', 'insertImageUpload'],
  ['redo', 'undo'],
]

export interface RichTextToolbarProps {
  toolbar: ReadonlyArray<readonly string[]>
  /** `name` on the toolbar element; the editor targets it by this. */
  name: string
  /**
   * ⚠️ THE WHOLE STRIP STARTS DISABLED AND STAYS THAT WAY UNTIL AN EDITOR TAKES FOCUS.
   * `scope.disabled = true` at link time (main.js:875), and every button carries
   * `ng-disabled="isDisabled()"` — so a composer nobody has clicked into shows thirteen faded
   * buttons, which is exactly what every capture of this screen has. Rendering them enabled
   * gives the right glyphs in the wrong colour, and it reads as a missing CSS rule rather than
   * as a state.
   */
  disabled?: boolean
  onTool?: (tool: string) => void
}

export function RichTextToolbar({ toolbar, name, disabled = true, onTool }: RichTextToolbarProps): ReactElement {
  return (
    <div className="ta-toolbar btn-toolbar" data-toolbar-name={name}>
      {toolbar.map((group) => (
        <div className="btn-group" key={group.join(',')}>
          {group.map((toolName) => {
            const tool = RICH_TEXT_TOOLS[toolName]
            return (
              <button
                key={toolName}
                type="button"
                // `scope.classes.toolbarButton` — "btn btn-default", from taOptions.
                className="btn btn-default"
                name={toolName}
                // `tabindex="-1"` matters: without it, clicking a button takes focus off the
                // editor and the selection the tool is about to act on is gone.
                tabIndex={-1}
                disabled={disabled}
                {...(tool?.title === undefined ? {} : { title: tool.title })}
                onClick={() => {
                  onTool?.(toolName)
                }}
              >
                {tool?.iconClass === undefined ? tool?.buttonText : <i className={tool.iconClass} />}
              </button>
            )
          })}
        </div>
      ))}
    </div>
  )
}
